import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useOrderEvents } from "../../lib/hooks/use-order-events";
import { useAdminStore } from "../../lib/hooks/use-store";
import Footer from "../home/components/Footer";
import AdminNavbar from "./AdminNavbar";
import AdminSidebar from "./AdminSidebar";

interface NewOrderData {
	orderId: number;
	status: string;
}

export default function AdminLayout({
	children,
}: {
	children: React.ReactNode;
}) {
	const queryClient = useQueryClient();
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const { t } = useTranslation();
	const { setSSEConnected } = useAdminStore();

	// Initialize audio
	useEffect(() => {
		const audio = new Audio("/static/media/bell.webm");
		audio.load();
		audioRef.current = audio;

		const unlock = () => {
			audio
				.play()
				.then(() => {
					audio.pause();
					audio.currentTime = 0;
					window.removeEventListener("click", unlock);
					window.removeEventListener("keydown", unlock);
				})
				.catch(() => {});
		};

		window.addEventListener("click", unlock);
		window.addEventListener("keydown", unlock);
		return () => {
			window.removeEventListener("click", unlock);
			window.removeEventListener("keydown", unlock);
		};
	}, []);

	const handleNewOrder = useCallback(
		(data: NewOrderData) => {
			// Play sound notification
			if (audioRef.current) {
				audioRef.current.currentTime = 0;
				audioRef.current.play().catch(() => {});
			}

			// Show toast notification
			toast.info(t("admin.common.new_order_toast"), {
				description: t("admin.common.order_id", { orderId: data?.orderId }),
				duration: 10000,
			});

			// Invalidate orders queries to refresh data
			queryClient.invalidateQueries({ queryKey: ["orders"] });
		},
		[queryClient, t],
	);

	// Connect to SSE - works on all admin pages
	const { isConnected } = useOrderEvents({
		onNewOrder: handleNewOrder,
		enabled: true,
	});

	// Update Zustand store with SSE connection state
	useEffect(() => {
		setSSEConnected(isConnected);
	}, [isConnected, setSSEConnected]);

	return (
		<div className="bg-background [--navbar-height:5rem]">
			<div className="h-(--navbar-height) border-b-4 border-black">
				<AdminNavbar />
			</div>
			<AdminSidebar />
			<div className="lg:ml-48 h-[calc(100dvh-var(--navbar-height))] @container/main">
				<div className="p-4 sm:p-6 md:p-8 border rounded-large bg-gradient-main min-h-full">
					{children}
				</div>
				<Footer />
			</div>
		</div>
	);
}
